import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, MapPin, Video, User, Clock, DollarSign } from "lucide-react";
import { Practitioner } from "@/hooks/use-practitioners";
import { getAvatarUrl } from "@/utils/storage";
import { getFullName } from "@/utils/name-helpers";
import { hasInPersonFormat, hasVirtualFormat } from "@/utils/session-format-helpers";

interface PractitionerCardProps {
  practitioner: Practitioner;
  isActive: boolean;
}

const PractitionerCard: React.FC<PractitionerCardProps> = React.memo(({ 
  practitioner, 
  isActive
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('practitioners');
  
  const {
    avatar_url,
    professional_title,
    bio,
    distance,
    average_rating = 0,
    total_reviews = 0,
    session_formats_offered = [],
    in_person_address,
    services = []
  } = practitioner;
  
  // Get full name using the utility
  const practitionerName = getFullName(practitioner);
  
  // Get formatted avatar URL
  const formattedAvatarUrl = avatar_url ? 
    getAvatarUrl('practitioner.photo', avatar_url) : 
    '';
  
  // Use standardized format checking
  const hasVirtual = hasVirtualFormat(session_formats_offered);
  const hasInPerson = hasInPersonFormat(session_formats_offered);
  
  // Calculate minimum price from services
  const getMinimumPrice = () => {
    if (!services || services.length === 0) return null;
    const activeServices = services.filter(service => service.price > 0);
    if (activeServices.length === 0) return null;
    const minPrice = Math.min(...activeServices.map(service => service.price));
    const currency = activeServices[0]?.currency || 'USD';
    return { price: minPrice, currency };
  };

  const minimumPrice = getMinimumPrice();

  // Handle click: always navigate to full page with practitioner slug
  const handleClick = () => {
    const identifier = practitioner.slug || practitioner.id;
    navigate(`/book-an-appointment/${identifier}`);
  };
  
  return (
    <Card 
      className={`cursor-pointer transition border hover:border-amber-300 w-full ${isActive ? 'ring-2 ring-amber-400 bg-amber-50/30' : ''}`}
      onClick={handleClick}
    >
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          {/* Avatar */}
          <Avatar 
            className="h-20 w-20 flex-shrink-0 mx-auto lg:mx-0 rounded-full"
            style={{ borderRadius: '50%' }}
          >
            {formattedAvatarUrl ? (
            <AvatarImage 
              src={formattedAvatarUrl} 
              alt={`${practitionerName || "Practitioner"} - Professional wellness practitioner`}
              crossOrigin="anonymous"
              loading="lazy"
              style={{ borderRadius: '50%' }}
              onError={(e) => {
                console.error("Failed to load avatar:", formattedAvatarUrl);
                e.currentTarget.style.display = 'none';
              }}
            />
            ) : (
              <AvatarFallback className="bg-amber-100 text-amber-800 rounded-full">
                <User size={32} />
              </AvatarFallback>
            )}
          </Avatar>
          
          {/* Main Content */}
          <div className="flex-1 min-w-0 text-center lg:text-left">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              {/* Left side - Basic info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-xl text-earth-900 mb-1" role="heading" aria-level={3}>
                  {practitionerName || t('unnamedPractitioner')}
                </h3>

                <div className="text-amber-700 font-medium mb-2">
                  {professional_title || t('spiritualPractitioner')}
                </div>
                
                {/* Platform Rating */}
                <div className="flex items-center justify-center lg:justify-start mb-2">
                  <Star fill="currentColor" className="h-4 w-4 text-amber-500 mr-1" />
                  <span className="font-medium">{typeof average_rating === 'number' ? average_rating.toFixed(1) : '-'}</span>
                  <span className="text-earth-600 ml-1">({total_reviews || 0} {t('reviews')})</span>
                </div>

                {/* Third-party Rating (if verified) - Mobile optimized */}
                {practitioner.third_party_rating_verified && practitioner.third_party_rating && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-center lg:justify-start mb-2 gap-1 sm:gap-2">
                    <div className="flex items-center justify-center lg:justify-start">
                      <Star fill="currentColor" className="h-4 w-4 text-blue-600 mr-1" />
                      <span className="font-medium">{practitioner.third_party_rating.toFixed(1)}</span>
                      <span className="text-earth-600 ml-1 text-sm">
                        ({practitioner.third_party_review_count || 0})
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-1 text-xs">
                      <span className="text-earth-500 text-center lg:text-left">
                        {practitioner.third_party_platform_name || '第三方平台'}
                      </span>
                      <span className="hidden sm:inline text-earth-400">•</span>
                      <span className="text-blue-700 font-medium bg-blue-50 px-2 py-1 rounded-full">
                        {t('verified')}
                      </span>
                    </div>
                  </div>
                )}

                {bio && (
                  <p className="text-sm text-earth-700 line-clamp-2 mb-3" aria-label="Practitioner bio">{bio}</p>
                )}

                {/* Location and Distance - Fixed mobile layout */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-earth-600 justify-center lg:justify-start">
                  {in_person_address && (
                    <div className="flex items-center justify-center lg:justify-start">
                      <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span className="truncate text-center lg:text-left max-w-full">{in_person_address}</span>
                    </div>
                  )}
                  
                  {typeof distance === 'number' && distance > 0 && (
                    <div className="flex items-center justify-center lg:justify-start font-medium text-earth-700">
                      <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span>{distance.toFixed(1)} {t('milesAway')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right side - Session options, price, and buttons */}
              <div className="flex flex-col items-center lg:items-end gap-3">
                {/* Minimum Price */}
                {minimumPrice && (
                  <div className="text-center lg:text-right">
                    <div className="text-lg font-semibold text-earth-900 flex items-center justify-center lg:justify-end">
                      <DollarSign className="h-4 w-4" />
                      {minimumPrice.price}
                    </div>
                    <div className="text-xs text-earth-600">{t('startingFrom')}</div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 justify-center lg:justify-end">
                  {hasVirtual && (
                    <Badge variant="secondary" className="bg-green-50 text-green-700 border border-green-200">
                      <Video className="h-3 w-3 mr-1" />
                      {t('virtual')}
                    </Badge>
                  )}
                  
                  {hasInPerson && (
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700 border border-blue-200">
                      <MapPin className="h-3 w-3 mr-1" />
                      {t('inPerson')}
                    </Badge>
                  )}
                </div>

                {/* Completed Orders Display */}
                {((practitioner as any).number_of_completed_orders !== undefined && (practitioner as any).number_of_completed_orders > 0) && (
                  <div className="flex items-center text-sm text-muted-foreground mt-2">
                    <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs font-medium">
                      {(practitioner as any).number_of_completed_orders} {(practitioner as any).number_of_completed_orders === 1 ? t('completedSession') : t('completedSessions')}
                    </span>
                  </div>
                )}
                
                {/* View Detail Button */}
                <div className="text-center lg:text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClick();
                    }}
                    className="font-bold border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 hover:text-amber-800 active:bg-amber-100 active:border-amber-500 transition-all duration-200"
                    aria-label={`View details for ${practitionerName || "practitioner"}`}
                  >
                    {t('viewDetail')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

PractitionerCard.displayName = 'PractitionerCard';

export default PractitionerCard;
